/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { Action } from "../../model/types";
import { CONTENT_DETAIL_SEED_TYPE } from "../../model/types";

export { CONTENT_DETAIL_SEED_TYPE } from "../../model/types";

/**
 * Discriminates the seed the Content Detail screen was opened with.
 *
 * Derived from the shared model constants and structurally compatible with
 * `ContentDetailSeedType` in ContentDetailProps.ts.
 */
export type ContentDetailSeedType =
  (typeof CONTENT_DETAIL_SEED_TYPE)[keyof typeof CONTENT_DETAIL_SEED_TYPE];

/** The seed the Content Detail screen was opened with. */
export interface ContentDetailSeed {
  seedType: ContentDetailSeedType;
  seedId: string;
}

/**
 * High-level content classification. Mirrors `ContentKind` in
 * ContentDetailProps.ts (kept independent — see {@link ContentDetailSeedType}).
 *
 * `program` / `game` exist so the offer and recording seeds can report the
 * screen type the actions response actually returned instead of collapsing
 * onto "movie" — see `deriveContentKind` in contentDetailSlice.ts.
 */
export type ContentDetailKind =
  | "movie"
  | "series"
  | "episode"
  | "program"
  | "game";

/**
 * Everything `RecordingsServiceInteractor.createOfferRecording` needs, lifted
 * off a single-recording action kernel at mapping time.
 *
 * The generated recording kernels have no shared parameter-bearing base type
 * (Android says the same in `extractSingleRecordingParams`), so the mapper
 * reads these structurally and stores them here rather than making the view
 * re-open the DTO.
 */
export interface ContentDetailRecordingParams {
  offerId?: string;
  contentId?: string;
  channelNumber?: string;
  channelId?: string;
  airingStartTime?: string;
  duration?: number;
  recordingValues?: Record<string, unknown>;
}

/** Backend recording choices with their localized labels and dependent values. */
export interface ContentDetailRecordingOptionGroup {
  id: string;
  label: string;
  choices: {
    label: string;
    values: Record<string, unknown>;
    disabledFields: string[];
  }[];
}

/**
 * Leading glyph drawn on an action pill — the "Presentation" column of
 * 01-actions.content.md / 01-actions.series.md.
 */
export type ContentDetailActionIconKind =
  | "play"
  | "record"
  | "recordCancel"
  | "stop"
  | "delete"
  | "favorite"
  | "favoriteFilled";

/** Live/linear offer line: "6/1 8:00-9:00pm", rating, resolution, call sign. */
export interface ContentDetailOfferSubtitleData {
  kind: "offer";
  dateTimeRange: string;
  /** `typeId + valueId` of the first internal rating; resolved into `rating` by the slice. */
  ratingKey?: string;
  rating?: string;
  resolution?: string;
  callSign?: string;
}

/** "Watch on {provider}" line. */
export interface ContentDetailProviderSubtitleData {
  kind: "provider";
  providerName: string;
}

/** Pre-resolved text, e.g. Streaming Options' "See all available streaming options". */
export interface ContentDetailVerbatimSubtitleData {
  kind: "verbatim";
  text: string;
}

/**
 * Line shown under the Watch Actions row while a pill holds focus — Android's
 * `WatchActionSubtitle`.
 */
export type ContentDetailActionSubtitleData =
  | ContentDetailOfferSubtitleData
  | ContentDetailProviderSubtitleData
  | ContentDetailVerbatimSubtitleData;

/**
 * Serializable mirror of `ContentDetailAction` (ContentDetailProps.ts) with
 * all function/ReactNode fields removed — `onSelect` and `icon` are
 * attached later by the View layer's Integration stage, never stored here.
 */
export interface ContentDetailActionData {
  id: string;
  label: string;
  variant: "primary" | "secondary";
  isDisabled: boolean;
  /** The originating `ActionKernelType` string, e.g. "watchLiveActionKernel". */
  kernelType: string;
  sourceAction?: Action;
  streamingOptions?: ContentDetailActionData[];
  iconKind?: ContentDetailActionIconKind;
  subtitle?: ContentDetailActionSubtitleData;
  /**
   * Present only on the eight single-recording kernels — see
   * {@link ContentDetailRecordingParams}.
   */
  recordingParams?: ContentDetailRecordingParams;
}

/**
 * Identifiers for the explore sections stacked below the action rows.
 *
 * Android TV renders every available section as its own header + rail
 * (`tv/DetailsScreen.kt` iterates `trailingTabs.forEach { ExploreSection }`)
 * and the Figma frames agree — there is no tab row. Order here is the
 * on-screen stacking order.
 */
export type ContentDetailSectionId =
  | "episodes"
  | "upcoming"
  | "cast"
  | "mayAlsoLike";

/** A visible explore section: its id and its (localized) header label. */
export interface ContentDetailSectionData {
  id: ContentDetailSectionId;
  label: string;
}

/** Serializable mirror of `ContentBadge` (ContentDetailProps.ts). */
export interface ContentDetailBadgeData {
  id: string;
  label: string;
  classStyle?: string;
}

/** Serializable mirror of `StreamingService` (ContentDetailProps.ts). */
export interface ContentDetailStreamingServiceData {
  id: string;
  name: string;
  logoUrl?: string;
  /** `actionId` of the `watchFromOttActionKernel` action behind this tile. */
  actionId?: string;
}

/** A selectable season / year / "Extra" chip above an explore rail. */
export interface ContentDetailSeasonData {
  /** Stringified `seasonOrYear` — Redux state keys must be serializable. */
  id: string;
  label: string;
  seasonOrYear: number;
}

/**
 * A single episode tile in the Episodes rail, mapped from the episode-guide
 * service's `EpisodeInfo`.
 */
export interface ContentDetailEpisodeData {
  /** Season-qualified episode tile key. */
  id: string;
  contentId?: string;
  title?: string;
  /** Card metadata line, e.g. "S1 E4" or the first-aired date. */
  subtitle?: string;
  /** Bottom-left overlay label drawn over the thumbnail, e.g. "S5:E14". */
  overlayLabel?: string;
  thumbnailUrl?: string;
  /** Watched progress in the range 0–1. */
  progress?: number;
  seasonOrYear?: number;
  episodeNumber?: number;
  isNew?: boolean;
  /** ISO-8601 first-aired date, kept for the focused-item metadata block. */
  firstAiredDate?: string;
  recordingId?: string;
  recordingStatus?: string;
}

/** Serializable mirror of `CastMember` (ContentDetailProps.ts). */
export interface ContentDetailCastData {
  id: string;
  name: string;
  role?: string;
  photoUrl?: string;
}

/**
 * A single upcoming airing in the Upcoming rail, mapped from the
 * upcoming-offers service's `UpcomingOfferV2`.
 */
export interface ContentDetailUpcomingData {
  /** `offerId` of the airing. */
  id: string;
  contentId?: string;
  title?: string;
  /** Start-time line, e.g. "8:00pm". */
  subtitle?: string;
  /** Card metadata line, e.g. "S1 E4 • HD" or the original air date. */
  schedule?: string;
  /** Bottom-left overlay label drawn over the thumbnail, e.g. "S5:E14". */
  overlayLabel?: string;
  imageUrl?: string;
  seasonOrYear?: number;
  episodeNumber?: number;
  isNew?: boolean;
  isLive?: boolean;
  /** ISO-8601 original air date, kept for the focused-item metadata block. */
  originalAirDate?: string;
}

/**
 * Serializable mirror of `RecommendedItem` (ContentDetailProps.ts) with
 * `onSelect` removed.
 */
export interface ContentDetailRecommendedData {
  id: string;
  /** Source feed type, used to select the content or collection API flow. */
  itemType?: string;
  title: string;
  imageUrl?: string;
}

/**
 * Lifecycle of a single explore section.
 *
 * Mirrors Android's per-section sealed states
 * (`EpisodeGuideSectionUiState` / `UpcomingSectionUiState` /
 * `MoreLikeThisSectionUiState`): a section that is not applicable to this
 * title is "hidden" and renders nothing at all, which is distinct from one
 * that loaded and came back empty.
 */
export type ContentDetailSectionStatus =
  | "hidden"
  | "loading"
  | "content"
  | "empty"
  | "error";

/** Common shape for every explore section's state. */
export interface ContentDetailSectionState<TItem> {
  status: ContentDetailSectionStatus;
  items: TItem[];
  /** True when the service may still have another page for this section. */
  hasMoreItems: boolean;
  /** True while a *subsequent* page is in flight (the rail stays visible). */
  isLoadingMore: boolean;
  error: string | null;
}

/**
 * Episode Guide section state.
 *
 * The rail shows the concatenation of every cached season in season order,
 * not just the selected one — selecting a season is a *scroll*, driven by
 * `seasonScrollRequestToken`, not a filter. See
 * `ExploreEpisodeGuideViewModel.handleSeasonSelected`.
 */
export interface ContentDetailEpisodeGuideState extends ContentDetailSectionState<ContentDetailEpisodeData> {
  requestId?: string;
  /** Available seasons, filtered to `>= 0`, de-duped and sorted descending. */
  seasons: ContentDetailSeasonData[];
  selectedSeasonOrYear: number | null;
  /** "season" | "year" from `SeasonsData.grouping`, when the service sends it. */
  grouping?: string;
  /** Per-season page cache, keyed by stringified `seasonOrYear`. */
  itemsBySeason: Record<string, ContentDetailEpisodeData[]>;
  /** Per-season "service may have more" flag, keyed the same way. */
  hasMoreBySeason: Record<string, boolean>;
  /**
   * Incremented on every season selection. The view scrolls the rail to the
   * first item of the selected season whenever this changes, so re-selecting
   * a cached season still re-scrolls.
   */
  seasonScrollRequestToken: number;
}

/**
 * Upcoming section state.
 *
 * The season chips here are derived client-side from the loaded airings
 * (Figma frame `2198-6084` shows them; Android's Upcoming section has none)
 * and behave like the Episode Guide's — a scroll, not a filter.
 */
export interface ContentDetailUpcomingState extends ContentDetailSectionState<ContentDetailUpcomingData> {
  seasons: ContentDetailSeasonData[];
  selectedSeasonOrYear: number | null;
  seasonScrollRequestToken: number;
}

/** Which screen region currently holds D-pad focus. */
export type ContentDetailFocusedRow = "watch" | "schedule" | "sections";

/** Which element of the focused explore section holds D-pad focus. */
export type ContentDetailSectionArea = "picker" | "rail";

/**
 * The carousel-item actions sheet (Figma `2198-6236` / `2219-938`, Android
 * `TvActionsBottomSheet`). `null` when closed.
 */
export interface ContentDetailSheetState {
  loadParams?: LoadContentDetailSheetParams;
  /** Section the item was selected from. */
  sectionId: ContentDetailSectionId;
  /**
   * Item id — `contentId` for episodes, `offerId` for upcoming airings,
   * `feedItemId` for a May Also Like recommendation.
   */
  itemId: string;
  title: string;
  subtitle?: string;
  metaLine?: string;
  description?: string;
  /** Atmospheric artwork generated from the selected preview content id. */
  imageUrl?: string;
  cast?: string;
  schedule?: string;
  badges?: string[];
  providerLogoUrl?: string;
  watchActions?: ContentDetailActionData[];
  scheduleActions?: ContentDetailActionData[];
  isLoading: boolean;
  error: string | null;
  actions: ContentDetailActionData[];
  focusedActionIndex: number;
}

/** Arguments used to load a selected explore item's actions sheet. */
export interface LoadContentDetailSheetParams {
  isRefresh?: boolean;
  sectionId: ContentDetailSectionId;
  itemId: string;
  /** Source feed type for collection routing when the id has no collection prefix. */
  itemType?: string;
  /** Content id for the preview request when `itemId` is an offer id. */
  contentId?: string;
  /** Card title rendered while the sheet request is in flight. */
  seedTitle?: string;
  /** Unformatted episode title used for the final type-aware sheet title. */
  episodeTitle?: string;
  /** Card subtitle rendered while the sheet request is in flight. */
  seedSubtitle?: string;
  /** Card metadata rendered while the sheet request is in flight. */
  seedMetaLine?: string;
  /** Episode season number used to identify an episode sheet tile. */
  seasonNumber?: number;
  /** Episode number used to identify an episode sheet tile. */
  episodeNumber?: number;
}

/**
 * Redux state for the Content Detail feature.
 *
 * Fully serializable — no functions, no ReactNode. The View layer's
 * Integration stage is responsible for attaching `onSelect` handlers and
 * icons to build the view-layer `ContentDetailProps` from this state.
 */
export interface ContentDetailState {
  /** The seed the screen was opened with. `null` before `loadContentDetail` runs. */
  seed: ContentDetailSeed | null;
  /** High-level classification. `null` until data has loaded. */
  kind: ContentDetailKind | null;
  /** True while `loadContentDetail` is in flight with nothing to show yet. */
  isLoading: boolean;
  /**
   * True while a *re-load* is in flight over an already-rendered payload
   * (e.g. after a recording is scheduled). The previous payload stays
   * visible instead of flashing the spinner — Android's `isRefreshing`.
   */
  isRefreshing: boolean;
  isRecordingPending: boolean;
  isFavoritePending?: boolean;
  /** Last error message from `loadContentDetail`. `null` when none. */
  error: string | null;
  /**
   * Composite catalog code for `error` (e.g. `"A716-G500"`, bare `"A716"`,
   * or `""` when there is no error). Drives the A716 UnityDialog — see
   * {@link ContentDetailErrorMapper} in contentDetailErrorMapper.ts.
   */
  errorCode: string;

  // ── Hero / identity ──────────────────────────────────────────────
  title?: string;
  contentType?: string;
  rating?: string;
  /** Numeric star rating from Preview (e.g. 3.5 out of 4). `null` when absent. */
  starRating: number | null;
  /** Release year shown in the type row, e.g. "2024". */
  releaseYear?: string;
  /** Runtime shown in the type row, e.g. "1h 30m". */
  duration?: string;
  genres: string[];
  description?: string;
  descriptionMaxLines?: number;
  /** Cast names (Preview synopsis line only — distinct from castMembers). */
  cast: string[];
  heroImageUrl?: string;
  heroImageAlt?: string;

  // ── Watch Actions row + availability ──────────────────────────────
  watchActions: ContentDetailActionData[];
  streamingServices: ContentDetailStreamingServiceData[];
  /**
   * WatchActionOfferText line — the "Next Airing: …" caption for a title
   * whose only airing is in the future.
   */
  upcomingOfferText?: string;

  // ── Schedule + inline badges ─────────────────────────────────────
  schedule?: string;
  badges: ContentDetailBadgeData[];
  networkLogoUrl?: string;
  networkName?: string;

  // ── Schedule Actions row ─────────────────────────────────────────
  scheduleActions: ContentDetailActionData[];

  // ── Explore sections ─────────────────────────────────────────────
  /** Visible sections, in stacking order. Derived from `detailList.details[]`. */
  sections: ContentDetailSectionData[];
  /**
   * Object ids the sections fetch with, derived from the *same*
   * `detailList.details[]` scan that derives `sections`. `null` when the
   * corresponding detail entry is absent.
   */
  episodeGuideObjectId: string | null;
  upcomingObjectId: string | null;
  mayAlsoLikeObjectId: string | null;

  episodeGuide: ContentDetailEpisodeGuideState;
  upcoming: ContentDetailUpcomingState;
  castMembers: ContentDetailSectionState<ContentDetailCastData>;
  mayAlsoLike: ContentDetailSectionState<ContentDetailRecommendedData>;

  // ── D-pad focus routing ──────────────────────────────────────────
  focusedRow: ContentDetailFocusedRow;
  /**
   * Position along the focused row. On the "watch" row that row is the whole
   * Watch strip — `watchActions` first, then one stop per entry in
   * `streamingServices` — so an index at or past `watchActions.length` is a
   * streaming-provider tile, not a pill. There is deliberately no separate
   * "focused provider" field: one index keeps the pills and the tiles a single
   * left/right traversal (Figma `2198-5609`, where an OTT-only title has no
   * pills at all and index 0 is already a tile).
   */
  focusedActionIndex: number;
  focusedSectionIndex: number;
  focusedSectionArea: ContentDetailSectionArea;
  focusedItemIndex: number;

  // ── Carousel-item actions sheet ──────────────────────────────────
  sheet: ContentDetailSheetState | null;
}
